import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { supportApi, notificationsApi, formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { 
  HelpCircle, Plus, MessageCircle, Clock, CheckCircle, 
  Loader2, ArrowRight, Send, Shield, User, Mail, AlertCircle, Bell
} from 'lucide-react';

const statusColors = {
  open: 'bg-yellow-100 text-yellow-700',
  replied: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-700'
};

const statusLabels = {
  open: 'Waiting for reply',
  replied: 'Replied',
  closed: 'Closed'
};

export default function SupportPage() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [filter, setFilter] = useState('all');
  const [newTicket, setNewTicket] = useState({ subject: '', message: '' });
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastData, setBroadcastData] = useState({ title: '', message: '' });
  const [broadcastSending, setBroadcastSending] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ticketsRes, adminRes] = await Promise.all([
          supportApi.getTickets(),
          supportApi.checkAdmin()
        ]);
        setTickets(ticketsRes.data);
        setIsAdmin(adminRes.data.is_admin);
      } catch (error) {
        console.error('Failed to fetch tickets:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject.trim() || !newTicket.message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await supportApi.createTicket(newTicket.subject, newTicket.message);
      setTickets(prev => [data, ...prev]);
      setNewTicket({ subject: '', message: '' });
      setShowNewTicket(false);
      toast.success('Support ticket created!');
      navigate(`/support/${data.id}`);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  };

  const filteredTickets = tickets.filter(t => {
    if (filter === 'all') return true;
    return t.status === filter;
  });

  const openCount = tickets.filter(t => t.status === 'open').length;
  const repliedCount = tickets.filter(t => t.status === 'replied').length;
  const closedCount = tickets.filter(t => t.status === 'closed').length;

  return (
    <div className="min-h-screen py-8" data-testid="support-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 
              className="text-3xl font-bold flex items-center gap-3"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {isAdmin ? (
                <Shield className="h-8 w-8 text-primary" />
              ) : (
                <HelpCircle className="h-8 w-8 text-primary" />
              )}
              {isAdmin ? 'Support Inbox' : 'Help & Support'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isAdmin ? 'View and respond to user support requests' : 'Get help from our support team'}
            </p>
          </div>
          {!isAdmin && (
            <Button 
              onClick={() => setShowNewTicket(true)}
              className="gap-2"
              data-testid="new-ticket-btn"
            >
              <Plus className="h-4 w-4" />
              New Request
            </Button>
          )}
          {isAdmin && (
            <Button 
              onClick={() => setShowBroadcast(!showBroadcast)}
              variant="outline"
              className="gap-2"
              data-testid="broadcast-btn"
            >
              <Bell className="h-4 w-4" />
              Send Notification
            </Button>
          )}
        </div>

        {/* Admin Broadcast Notification Form */}
        {isAdmin && showBroadcast && (
          <Card className="mb-6 border-blue-200 bg-blue-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Bell className="h-5 w-5 text-blue-600" />
                Send Notification to All Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!broadcastData.title.trim() || !broadcastData.message.trim()) {
                  toast.error('Please fill in both title and message');
                  return;
                }
                setBroadcastSending(true);
                try {
                  await notificationsApi.sendAdmin(broadcastData.title, broadcastData.message);
                  toast.success('Notification sent to all users!');
                  setBroadcastData({ title: '', message: '' });
                  setShowBroadcast(false);
                } catch (err) {
                  toast.error(formatApiError(err));
                } finally {
                  setBroadcastSending(false);
                }
              }} className="space-y-3">
                <Input
                  value={broadcastData.title}
                  onChange={(e) => setBroadcastData(d => ({ ...d, title: e.target.value }))}
                  placeholder="Notification title"
                  data-testid="broadcast-title"
                />
                <Textarea
                  value={broadcastData.message}
                  onChange={(e) => setBroadcastData(d => ({ ...d, message: e.target.value }))}
                  placeholder="Message to all users..."
                  rows={3}
                  data-testid="broadcast-message"
                />
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowBroadcast(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" size="sm" disabled={broadcastSending} data-testid="send-broadcast-btn">
                    {broadcastSending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    Send to All
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Admin Stats */}
        {isAdmin && (
          <div className="grid grid-cols-3 gap-4 mb-6" data-testid="admin-stats">
            <button
              onClick={() => setFilter(filter === 'open' ? 'all' : 'open')}
              className={`p-4 rounded-xl border text-left transition-colors ${
                filter === 'open' ? 'border-yellow-400 bg-yellow-50' : 'border-border hover:bg-muted/50'
              }`}
              data-testid="filter-open"
            >
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <span className="text-sm font-medium text-muted-foreground">Needs Reply</span>
              </div>
              <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{openCount}</p>
            </button>
            <button
              onClick={() => setFilter(filter === 'replied' ? 'all' : 'replied')}
              className={`p-4 rounded-xl border text-left transition-colors ${
                filter === 'replied' ? 'border-green-400 bg-green-50' : 'border-border hover:bg-muted/50'
              }`}
              data-testid="filter-replied"
            >
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-muted-foreground">Replied</span>
              </div>
              <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{repliedCount}</p>
            </button>
            <button
              onClick={() => setFilter(filter === 'closed' ? 'all' : 'closed')}
              className={`p-4 rounded-xl border text-left transition-colors ${
                filter === 'closed' ? 'border-gray-400 bg-gray-50' : 'border-border hover:bg-muted/50'
              }`}
              data-testid="filter-closed"
            >
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-muted-foreground">Closed</span>
              </div>
              <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{closedCount}</p>
            </button>
          </div>
        )}

        {/* New Ticket Form */}
        {showNewTicket && (
          <Card className="mb-8 border-primary/20 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                New Support Request
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitTicket} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    value={newTicket.subject}
                    onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                    placeholder="What do you need help with?"
                    data-testid="ticket-subject"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    value={newTicket.message}
                    onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                    placeholder="Describe your issue or question in detail..."
                    rows={4}
                    data-testid="ticket-message"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowNewTicket(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} data-testid="submit-ticket-btn">
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Request
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Tickets List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>
                {isAdmin ? 'All Tickets' : 'Your Requests'} ({filteredTickets.length})
              </span>
              {isAdmin && filter !== 'all' && (
                <Button variant="ghost" size="sm" onClick={() => setFilter('all')}>
                  Show All
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredTickets.length > 0 ? (
              <div className="space-y-3">
                {filteredTickets.map((ticket) => (
                  <Link
                    key={ticket.id}
                    to={`/support/${ticket.id}`}
                    className={`flex items-center gap-4 p-4 border rounded-xl hover:bg-muted/50 transition-colors ${
                      isAdmin && ticket.status === 'open' ? 'border-yellow-300 bg-yellow-50/50' : 'border-border'
                    }`}
                    data-testid={`ticket-${ticket.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isAdmin && ticket.status === 'open' ? 'bg-yellow-100' : 'bg-primary/10'
                    }`}>
                      {isAdmin && ticket.status === 'open' ? (
                        <AlertCircle className="h-5 w-5 text-yellow-600" />
                      ) : (
                        <MessageCircle className="h-5 w-5 text-primary" />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium truncate">{ticket.subject}</p>
                        <Badge className={statusColors[ticket.status] || 'bg-gray-100'}>
                          {statusLabels[ticket.status] || ticket.status}
                        </Badge>
                      </div>
                      {isAdmin && (
                        <div className="flex items-center gap-3 text-sm text-muted-foreground mb-1">
                          {ticket.user_name && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {ticket.user_name}
                            </span>
                          )}
                          {ticket.user_email && (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {ticket.user_email}
                            </span>
                          )}
                        </div>
                      )}
                      {ticket.last_message && (
                        <p className="text-sm text-muted-foreground truncate">
                          {ticket.last_message}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-sm text-muted-foreground flex-shrink-0">
                      <Clock className="h-4 w-4" />
                      {formatTime(ticket.last_message_at)}
                    </div>

                    <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <HelpCircle className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">
                  {isAdmin 
                    ? (filter !== 'all' ? `No ${filter} tickets` : 'No support tickets yet')
                    : 'No support requests yet'
                  }
                </p>
                {!isAdmin && (
                  <Button onClick={() => setShowNewTicket(true)} data-testid="create-first-ticket">
                    Create Your First Request
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Info */}
        {!isAdmin && (
          <div className="mt-8 p-6 bg-muted/50 rounded-2xl">
            <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              How can we help?
            </h3>
            <p className="text-sm text-muted-foreground">
              Our support team typically responds within a few hours. You can ask about listings, 
              account issues, payment questions, or anything else related to HabeshaTown.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
